import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstant from "../../api";
import {
    publicProposalDetailEndpoint,
    publicProposalEmailEndpoint,
} from "../../api/endpoints";
import { ObjectiveProposalFlow } from "../../components/ProposalFlow/ObjectiveProposalFlow";
import { ProposalDisclaimer } from "../../components/ProposalFlow/ProposalDisclaimer";
import {
    downloadProposalPdf,
    MAX_EMAIL_PDF_BASE64_CHARS,
    pdfInputFromDetail,
    proposalPdfBase64,
} from "../../components/ProposalFlow/pdf/downloadProposalPdf";
import { apiErrorCode } from "../../utils/apiError";
import { ProposalStrategyView } from "../../components/ProposalFlow/strategy/ProposalStrategyView";
import { strategyFromProposal } from "../../components/ProposalFlow/strategy/StrategyData";
import { useProposalDetailPreview } from "../../components/ProposalFlow/useProposalDetailPreview";
import { useProposalExamples } from "../../components/ProposalFlow/useProposalExamples";
import { ProposalDetail, ProposalExamplesResponse } from "../../components/ProposalFlow/types";
import { useUser } from "../../hooks/useUser";
import { hideAllPricing, isInternal } from "../../utils";
import { formatCurrency } from "../../utils/formatCurrency";
import { ANON_PRICE_TEASER } from "../../components/ProposalFlow/strategyCopy";
import { buildRegisterHandoffPath } from "../../utils/funnelHandoff";

// Public lead-facing proposal page (/proposal/view/:token) — same bare shell
// as the wizard page (pdp-audit style, no portal chrome). The lead can view
// their strategy, download/email the PDF, and — while the proposal hasn't
// been confirmed — edit it in place via the wizard.

const PAGE_BG = "#FAFAF8";
const FONT = "'DM Sans', sans-serif";
const BLACK = "#1C1A17";
const GOLD_DARK = "#A07B2E";
const TEXT_MUTED = "#7A7268";
const BORDER = "#E5E1DB";

const actionButton: React.CSSProperties = {
    fontFamily: FONT,
    fontSize: 13.5,
    fontWeight: 600,
    borderRadius: 10,
    padding: "11px 20px",
    cursor: "pointer",
    border: "none",
};

export default function ProposalViewPage() {
    const { token } = useParams<{ token: string }>();
    const { user, role } = useUser();
    const internalViewer = !!user && isInternal(role);
    // Any authenticated session (internal, brand — RMN users open this same
    // page from their proposals list) sees real pricing; anonymous token
    // holders get the teaser line instead. The API redacts the anonymous
    // payload too — this flag just picks the copy (TDC-1563).
    const authedViewer = !!user;

    const [detail, setDetail] = useState<ProposalDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [editing, setEditing] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [emailing, setEmailing] = useState(false);
    const [emailed, setEmailed] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const preview = useProposalDetailPreview(detail);
    const examples = useProposalExamples(
        detail && detail.product_category ? detail.product_category.id : null,
        detail ? detail.content_type_units.map((u) => u.content_type_id) : [],
        !!detail,
    );

    // Attached-shortlist videos ride along as extra examples (strip + PDF).
    const mergedExamples = React.useMemo<ProposalExamplesResponse | null>(() => {
        const shortlistVideos = detail?.video_shortlist?.videos || [];
        if (!examples && shortlistVideos.length === 0) return examples;
        const videos = [...(examples?.videos || [])];
        shortlistVideos.forEach((v) => {
            if (!videos.some((x) => x.id === v.id)) videos.push(v);
        });
        return {
            product_category_id: examples?.product_category_id ?? 0,
            videos,
            experts: examples?.experts || [],
        };
    }, [examples, detail]);

    useEffect(() => {
        document.body.classList.add("pdp-audit-embed");
        return () => {
            document.body.classList.remove("pdp-audit-embed");
        };
    }, []);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setNotFound(false);
        try {
            const { data } = await axiosInstant.get<ProposalDetail>(
                publicProposalDetailEndpoint(token),
            );
            setDetail(data);
        } catch {
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        load();
    }, [load]);

    const shareUrl = token ? `${window.location.origin}/proposal/view/${token}` : null;

    // TDC-1591: an RMN proposal (detail.brand.rmn) shows no pricing — and no
    // "$20k" teaser — to anyone, internal viewers included; RMN viewers are
    // hidden by the viewer fallback even while detail is still loading.
    const pricingHidden = hideAllPricing(role, user, detail);

    const download = async () => {
        if (!detail || downloading) return;
        setDownloading(true);
        try {
            await downloadProposalPdf(
                pdfInputFromDetail(detail, {
                    internal: internalViewer,
                    showPricing: authedViewer && !pricingHidden,
                    showPriceTeaser: !pricingHidden,
                    preview,
                    shareUrl,
                    examples: mergedExamples,
                }),
            );
        } finally {
            setDownloading(false);
        }
    };

    const emailMe = async () => {
        if (!detail || !token || emailing || emailed) return;
        setEmailing(true);
        setEmailError(null);
        try {
            const pdfBase64 = await proposalPdfBase64(
                pdfInputFromDetail(detail, {
                    internal: false,
                    showPricing: authedViewer && !pricingHidden,
                    showPriceTeaser: !pricingHidden,
                    preview,
                    shareUrl,
                    examples: mergedExamples,
                }),
            );
            // Server rejects attachments over 8MB of base64 (413) — skip the
            // doomed upload and point at Download instead.
            if (pdfBase64.length > MAX_EMAIL_PDF_BASE64_CHARS) {
                setEmailError('This strategy is too large to email — please use Download PDF instead.');
                return;
            }
            await axiosInstant.post(publicProposalEmailEndpoint(token), {
                pdfBase64,
                filename: 'video-strategy.pdf',
            });
            setEmailed(true);
        } catch (err: any) {
            const code = apiErrorCode(err);
            if (code === 'PdfTooLarge') {
                setEmailError('This strategy is too large to email — please use Download PDF instead.');
            } else if (code === 'NoRecipientOnFile') {
                setEmailError('We don\'t have an email address on file for this strategy.');
            } else {
                setEmailError('We couldn\'t send the email. Please try again, or use Download PDF.');
            }
        } finally {
            setEmailing(false);
        }
    };

    const editable = !!detail && detail.status === 'proposal';

    // Header subline: anonymous viewers get the teaser copy instead of the
    // real total (the API nulls it for them anyway — TDC-1563). RMN
    // proposals/viewers (TDC-1591) keep the neutral default — no total, no
    // teaser — even when anonymous.
    let pricingSubline = 'Final quote confirmed by our team';
    if (!pricingHidden && !authedViewer) {
        pricingSubline = `${ANON_PRICE_TEASER} · final quote confirmed by our team`;
    } else if (!pricingHidden && detail && detail.pricing.total != null) {
        pricingSubline = `Estimated investment ${formatCurrency(detail.pricing.total)} · final quote confirmed by our team`;
    }

    return (
        <div style={{ minHeight: "100vh", background: PAGE_BG, fontFamily: FONT }}>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');`}</style>
            <div style={{ width: 1160, maxWidth: "100%", margin: "0 auto", padding: "44px 20px" }}>
                {loading && (
                    <div style={{ textAlign: "center", color: TEXT_MUTED, fontSize: 14, padding: "60px 0" }}>
                        Loading your strategy…
                    </div>
                )}

                {!loading && notFound && (
                    <div style={{ textAlign: "center", padding: "70px 0" }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: BLACK, marginBottom: 8 }}>
                            This strategy link isn't valid
                        </div>
                        <div style={{ fontSize: 14, color: TEXT_MUTED, marginBottom: 20 }}>
                            The link may have been mistyped or the strategy removed. You can build a new
                            strategy in a couple of minutes.
                        </div>
                        <a
                            href="/proposal"
                            style={{ ...actionButton, background: BLACK, color: "#fff", textDecoration: "none" }}
                        >
                            Build a new strategy →
                        </a>
                    </div>
                )}

                {!loading && detail && editing && (
                    <ObjectiveProposalFlow
                        edit={{ proposal: detail, endpoint: publicProposalDetailEndpoint(token || "") }}
                        onSaved={(updated) => {
                            setDetail(updated);
                            setEditing(false);
                            setEmailed(false);
                        }}
                        onCancelEdit={() => setEditing(false)}
                    />
                )}

                {!loading && detail && !editing && (
                    <>
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 14,
                                marginBottom: 22,
                            }}
                        >
                            <div style={{ minWidth: 0 }}>
                                <div
                                    style={{
                                        fontSize: 11.5,
                                        fontWeight: 700,
                                        letterSpacing: 0.6,
                                        color: GOLD_DARK,
                                        textTransform: "uppercase",
                                        marginBottom: 4,
                                    }}
                                >
                                    Your video strategy
                                </div>
                                <h1 style={{ fontSize: 24, fontWeight: 700, color: BLACK, margin: 0 }}>
                                    {detail.name}
                                </h1>
                                <div style={{ fontSize: 13, color: TEXT_MUTED, marginTop: 6 }}>
                                    {pricingSubline}
                                </div>
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                                <button
                                    type="button"
                                    onClick={download}
                                    disabled={downloading}
                                    style={{ ...actionButton, background: BLACK, color: "#fff" }}
                                >
                                    {downloading ? "Preparing…" : "Download PDF ↓"}
                                </button>
                                <button
                                    type="button"
                                    onClick={emailMe}
                                    disabled={emailing || emailed}
                                    style={{
                                        ...actionButton,
                                        background: "transparent",
                                        color: emailed ? "#2E7D32" : GOLD_DARK,
                                        border: `1px solid ${emailed ? "#2E7D32" : GOLD_DARK}`,
                                    }}
                                >
                                    {emailed
                                        ? "✓ Sent to your inbox"
                                        : emailing
                                            ? "Sending…"
                                            : "Email me this strategy"}
                                </button>
                                {emailError && (
                                    <span style={{ fontSize: 13, color: "#c62828", alignSelf: "center" }}>
                                        {emailError}
                                    </span>
                                )}
                                {editable && (
                                    <button
                                        type="button"
                                        onClick={() => setEditing(true)}
                                        style={{
                                            ...actionButton,
                                            background: "transparent",
                                            color: TEXT_MUTED,
                                            border: `1px solid ${BORDER}`,
                                        }}
                                    >
                                        Edit strategy
                                    </button>
                                )}
                            </div>
                        </div>

                        {!editable && (
                            <div
                                style={{
                                    fontSize: 13,
                                    color: TEXT_MUTED,
                                    background: "#F4F0E9",
                                    border: `1px solid ${BORDER}`,
                                    borderRadius: 10,
                                    padding: "10px 14px",
                                    marginBottom: 18,
                                }}
                            >
                                This strategy has been confirmed — our team is on it. It can no longer be
                                edited, but the PDF and this page stay available.
                            </div>
                        )}

                        <ProposalStrategyView
                            data={strategyFromProposal(detail)}
                            showPricingBreakdowns={internalViewer && !pricingHidden}
                            perContentTypeEstimates={
                                internalViewer && !pricingHidden && detail.tier && preview && preview.tiers[detail.tier]
                                    ? preview.tiers[detail.tier]!.per_content_type_estimates || null
                                    : null
                            }
                            examples={mergedExamples}
                            footer={
                                <div
                                    style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 12,
                                        background: "#fff",
                                        border: `1px solid ${BORDER}`,
                                        borderRadius: 14,
                                        padding: "16px 18px",
                                    }}
                                >
                                    <div>
                                        <div style={{ fontSize: 14.5, fontWeight: 700, color: BLACK }}>
                                            Ready to bring this strategy to life?
                                        </div>
                                        <div style={{ fontSize: 12.5, color: TEXT_MUTED, marginTop: 2 }}>
                                            Our team will create a custom video plan tailored to your products,
                                            audience, and goals. We'll respond within 1 business day.
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                                        {/* Anonymous leads on an unclaimed strategy: register with
                                            it in tow (TDC-1573). */}
                                        {!user && !detail.brand && (
                                            <a
                                                href={buildRegisterHandoffPath({
                                                    email: detail.lead ? detail.lead.email : undefined,
                                                    url: detail.proposal_meta
                                                        ? detail.proposal_meta.product_url
                                                        : undefined,
                                                    auditReportId: detail.pdp_audit_report
                                                        ? detail.pdp_audit_report.id
                                                        : undefined,
                                                    strategyToken: token,
                                                })}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    ...actionButton,
                                                    background: BLACK,
                                                    color: "#fff",
                                                    textDecoration: "none",
                                                }}
                                            >
                                                Create your free account →
                                            </a>
                                        )}
                                        <a
                                            href="mailto:sales@thedesirecompany.com?subject=Video%20strategy"
                                            style={{
                                                ...actionButton,
                                                textDecoration: "none",
                                                ...(!user && !detail.brand
                                                    ? {
                                                        background: "transparent",
                                                        color: GOLD_DARK,
                                                        border: `1px solid ${GOLD_DARK}`,
                                                    }
                                                    : { background: BLACK, color: "#fff" }),
                                            }}
                                        >
                                            Talk to a strategist →
                                        </a>
                                    </div>
                                </div>
                            }
                        />
                    </>
                )}
                {!loading && !notFound && <ProposalDisclaimer />}
            </div>
        </div>
    );
}
